import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, DownloadSimple, ShareFat } from '@phosphor-icons/react';
import { Button } from './ui/button';

const IOS_BANNER_SHOWN_KEY = 'ato_ios_banner_shown';

const getIsStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const getIsIOS = () => {
  const ua = window.navigator.userAgent;
  const isIOSDevice = /iPhone|iPad|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS/.test(ua);
  return isIOSDevice && isSafari;
};

/* PWA install prompt — pure addition, mounted inside Footer (see Footer.js)
   in place of the old App Store / Google Play badges. Renders nothing on
   desktop, if the app is already installed, or on unsupported browsers. */
export const InstallPWA = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (getIsStandalone()) return;

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    if (getIsIOS() && !localStorage.getItem(IOS_BANNER_SHOWN_KEY)) {
      setShowIos(true);
      localStorage.setItem(IOS_BANNER_SHOWN_KEY, 'true');
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  if (getIsStandalone() || dismissed) return null;

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  // Android/Chrome — native install prompt available
  if (deferredPrompt) {
    return (
      <div
        className="md:hidden fixed left-0 right-0 bottom-[72px] z-40 bg-white border-t border-[#eee] px-4 py-3 flex items-center gap-3 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]"
        data-testid="install-pwa-banner"
      >
        <img src="/icons/icon-192.png" alt="ATÔ" className="w-10 h-10 rounded-lg flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text_main leading-tight" data-testid="install-pwa-title">
            {t('pwa.install_title')}
          </p>
          <p className="text-xs text-text_muted leading-tight truncate">{t('pwa.install_subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-xs text-text_muted px-2 flex-shrink-0"
          data-testid="install-pwa-later-link"
        >
          {t('pwa.later')}
        </button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleInstallClick}
          className="gap-1.5 flex-shrink-0"
          data-testid="install-pwa-install-btn"
        >
          <DownloadSimple size={16} weight="bold" />
          {t('pwa.install_btn')}
        </Button>
      </div>
    );
  }

  // iOS/Safari — no native prompt exists, show manual "Add to Home Screen" steps
  if (showIos) {
    return (
      <div
        className="md:hidden fixed left-0 right-0 bottom-[72px] z-40 bg-white border-t border-[#eee] px-4 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]"
        data-testid="install-pwa-ios-banner"
      >
        <div className="flex items-start gap-3">
          <img src="/icons/icon-192.png" alt="ATÔ" className="w-10 h-10 rounded-lg flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-text_main leading-tight mb-1.5">{t('pwa.install_title')}</p>
            <ol className="text-xs text-text_muted space-y-1 list-decimal list-inside">
              <li>
                <span className="inline-flex items-center gap-1">
                  <ShareFat size={13} className="inline text-primary flex-shrink-0" />
                  {t('pwa.ios_step1')}
                </span>
              </li>
              <li>{t('pwa.ios_step2')}</li>
              <li>{t('pwa.ios_step3')}</li>
            </ol>
          </div>
          <button
            type="button"
            onClick={() => setShowIos(false)}
            className="text-text_muted flex-shrink-0 p-1"
            data-testid="install-pwa-ios-close-btn"
            aria-label={t('pwa.later')}
          >
            <X size={18} />
          </button>
        </div>
      </div>
    );
  }

  return null;
};
